import { useState, useCallback, useEffect, useRef } from 'react'

import { useLazyQuery } from '@apollo/client'
import {
  Box,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
  Tooltip,
} from '@mui/material'
import {
  IconArrowLeft,
  IconChartColumn,
  IconPlus,
  IconRefresh,
  IconTestPipe,
  IconX,
} from '@tabler/icons-react'
import toast from 'react-hot-toast'

import { useMutation, useQuery } from '@redwoodjs/web'

import MetadataChip from 'src/components/Chips/MetadataChip/MetadataChip'
import ObjectChip from 'src/components/Chips/ObjectChip/ObjectChip'
import { GET_WORKSPACE_OBJECTS_BY_IDS } from 'src/components/Objects/queries'
import { BACKFILL_PROPERTY_DEFINITION } from 'src/components/Properties/mutations'
import PropertyEdit from 'src/components/Properties/PropertyEdit'
import PropertyHistoryEntries from 'src/components/Properties/PropertyHistoryEntries'
import {
  PROPERTY_DEFINITION_HISTORY,
  PROPERTY_DEFINITION_RUN_HISTORY,
} from 'src/components/Properties/queries'
import Row from 'src/components/Row/Row'
import SearchModal from 'src/components/Search/SearchModal/SearchModal'
import WorkspaceMemberChip from 'src/components/WorkspaceMemberChip/WorkspaceMemberChip'
import { dayjs } from 'src/lib/dayjs'
import { logger } from 'src/lib/logger'

import SidebarButton from '../SidebarButton/SidebarButton'

import TestGuidance from './TestGuidance'
import TestResultsReview from './TestResultsReview'

const statusLabels = {
  PENDING: 'Processing',
  COMPLETED: 'Completed',
  FAILED: 'Errored',
}

const PropertyTest = ({ propertyDefinition, workspaceId, onSwitchToEdit }) => {
  const [testCreateOpen, setTestCreateOpen] = useState(false)
  const [testObjectIds, setTestObjectIds] = useState<string[]>([])
  const [testObjectSearchOpen, setTestObjectSearchOpen] = useState(false)
  const [selectedRunId, setSelectedRunId] = useState(null)
  const [propertyEditState, setPropertyEditState] = useState({
    open: false,
    objectId: null,
    value: null,
    targetId: null,
    propertyValue: null,
    loading: false,
  })
  const isFirstVisitRef = useRef(true)
  const [showGuidance, setShowGuidance] = useState(true)
  const [latestTestRunId, setLatestTestRunId] = useState(null)
  const [showTestResults, setShowTestResults] = useState(false)
  const [hasSuccessfulTest, setHasSuccessfulTest] = useState(false)

  // Track first visit
  useEffect(() => {
    if (isFirstVisitRef.current) {
      isFirstVisitRef.current = false
    }
  }, [])

  // Moved below where backfills is defined

  // Query for general history (when no run is selected)
  const {
    data: generalHistoryData,
    loading: generalHistoryLoading,
    refetch: refetchGeneralHistory,
  } = useQuery(PROPERTY_DEFINITION_HISTORY, {
    variables: {
      workspaceId,
      propertyDefinitionId: propertyDefinition.id,
    },
    skip: !propertyDefinition.id || !workspaceId || !!selectedRunId,
    pollInterval: 3000,
  })

  // Query for run-specific history (when a run is selected)
  const {
    data: runHistoryData,
    loading: runHistoryLoading,
    refetch: refetchRunHistory,
  } = useQuery(PROPERTY_DEFINITION_RUN_HISTORY, {
    variables: {
      workspaceId,
      propertyDefinitionId: propertyDefinition.id,
      runId: selectedRunId || '',
    },
    skip: !propertyDefinition.id || !workspaceId || !selectedRunId,
    pollInterval: 3000,
    onCompleted: (data) => {
      logger.dev('Debug log', { value: { runHistoryData: data } })
    },
  })

  // Combine the data from both queries
  const data = selectedRunId ? runHistoryData : generalHistoryData
  const loading = selectedRunId ? runHistoryLoading : generalHistoryLoading
  const refetch = selectedRunId ? refetchRunHistory : refetchGeneralHistory

  const [runTest] = useMutation(BACKFILL_PROPERTY_DEFINITION)

  const [fetchObjectData] = useLazyQuery(GET_WORKSPACE_OBJECTS_BY_IDS, {
    onCompleted: (data) => {
      if (data?.workspaceObjectsByIds?.length > 0) {
        const object = data.workspaceObjectsByIds[0]
        const propertyValue =
          object.properties?.custom?.[propertyDefinition.id] ||
          object.properties?.standard?.[propertyDefinition.id] ||
          null

        logger.dev('Fetched object data for property edit', {
          object,
          propertyValue,
          propertyDefinitionId: propertyDefinition.id,
        })

        setPropertyEditState((prevState) => ({
          ...prevState,
          propertyValue,
          loading: false,
          open: true,
        }))
      } else {
        toast.error('Could not find object data')
        setPropertyEditState((prevState) => ({
          ...prevState,
          loading: false,
        }))
      }
    },
    onError: (error) => {
      logger.error('Error fetching object data', error)
      toast.error('Error fetching object data')
      setPropertyEditState((prevState) => ({
        ...prevState,
        loading: false,
      }))
    },
  })

  const handlePropertyValueClick = useCallback(
    (objectId, value, elementId) => {
      // First set loading state
      setPropertyEditState({
        open: false,
        objectId,
        value: { value }, // This will be overwritten when the server returns and opens the PropertyEdit menu
        targetId: elementId,
        propertyValue: null,
        loading: true,
      })

      // Then fetch the full object data to get the current property value
      toast.promise(
        fetchObjectData({
          variables: {
            workspaceId,
            objectType: propertyDefinition.objectTypeId,
            objectIds: [objectId],
          },
        }),
        {
          loading: 'Getting current property value...',
          error: 'Error fetching current property value',
        }
      )

      logger.dev('Fetching object data for property edit', {
        objectId,
        value,
        elementId,
        propertyDefinition,
      })
    },
    [fetchObjectData, propertyDefinition, workspaceId]
  )

  const handlePropertyEditClose = useCallback(() => {
    setPropertyEditState({
      open: false,
      objectId: null,
      value: null,
      targetId: null,
      propertyValue: null,
      loading: false,
    })
  }, [])

  const handleRunTest = async () => {
    // Log the test parameters
    logger.dev('Running test with params:', {
      workspaceId,
      objectType: propertyDefinition.objectTypeId,
      propertyDefinitionId: propertyDefinition.id,
      testObjectIds,
    })

    toast.promise(
      runTest({
        variables: {
          workspaceId,
          objectType: propertyDefinition.objectTypeId,
          propertyDefinitionId: propertyDefinition.id,
          testObjectIds,
        },
      }),
      {
        loading: 'Running test...',
        success: (data) => {
          refetch()
          setTestCreateOpen(false)
          setShowTestResults(true)
          // Track the test run ID if available
          if (data?.backfillObjectProperty) {
            setLatestTestRunId(data.backfillObjectProperty)
          }
          return 'Test is running'
        },
        error: (error) => {
          logger.error('Test run failed:', error)
          return 'Error running test'
        },
      }
    )
  }

  const handleRunBackfill = async () => {
    toast.promise(
      runTest({
        variables: {
          workspaceId,
          objectType: propertyDefinition.objectTypeId,
          propertyDefinitionId: propertyDefinition.id,
        },
      }),
      {
        loading: 'Setting up backfill...',
        success: () => {
          refetch()
          return 'Backfill is running'
        },
        error: 'Failed to run backfill',
      }
    )
  }

  const handleViewRunDetails = (backfillId) => {
    setSelectedRunId(backfillId)
  }

  const handleBackToAllHistory = () => {
    setSelectedRunId(null)
  }

  const updates = data?.propertyDefinitionHistory?.updates
  const backfills = data?.propertyDefinitionHistory?.backfills

  // Check for successful test runs
  useEffect(() => {
    if (backfills && backfills.length > 0) {
      const hasTest = backfills.some(
        (backfill) =>
          backfill.metadata?.isTest && backfill.status === 'COMPLETED'
      )
      setHasSuccessfulTest(hasTest)
    }
  }, [backfills])

  // Compute test results from the latest test run
  const testResults = React.useMemo(() => {
    if (!latestTestRunId || !updates) return []

    // Filter updates for the latest test run
    const testUpdates = updates.filter(
      (update) => update.runId === latestTestRunId
    )

    // Group by objectId to get unique results
    const resultsByObject = testUpdates.reduce((acc, update) => {
      if (!acc[update.objectId]) {
        acc[update.objectId] = {
          objectId: update.objectId,
          success: !!update.value,
          value: update.value,
          reasoning: update.reasoning,
          error: !update.value ? 'No value generated' : undefined,
        }
      }
      return acc
    }, {})

    const results = Object.values(resultsByObject)

    // Update hasSuccessfulTest if we have good test results
    if (results.length > 0) {
      const successCount = results.filter((r) => r.success).length
      const successRate = (successCount / results.length) * 100
      if (successRate >= 80) {
        setHasSuccessfulTest(true)
      }
    }

    return results
  }, [latestTestRunId, updates])

  const containerSx = {
    '& .property-header': {
      justifyContent: 'space-between',
      gap: 2,
      mt: 0,
      mb: 2,
      '&.top': {
        mt: 0,
        p: 1.5,
        border: (theme) => `1px solid ${theme.palette.divider}`,
      },
      '&.update-history-header': {
        mt: 4,
      },
    },
    '& .property-header-buttons': {
      gap: 1,
    },
    '& .property-test-description': {
      color: 'text.secondary',
      textOverflow: 'ellipsis',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
    },
    '& h2, h3': {
      flexShrink: 0,
      p: 0,
      m: 0,
    },
    '& .property-update-row': {
      gap: 2,
    },
    '& .property-update-object-chip': {
      flexShrink: 1,
    },
    '& .property-update-value': {
      fontWeight: 600,
      fontSize: '11px',
      letterSpacing: '-0.2px',
      maxWidth: '160px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    '& .property-update-reasoning': {
      textOverflow: 'ellipsis',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      fontSize: '11px',
      maxWidth: '280px',
      flexShrink: 1,
    },

    '& .MuiTableCell-root': {
      pl: 0,
      pr: '12px',
      py: '6px',
      fontSize: '11px',
      letterSpacing: '-0.2px',
      '&:last-child': {
        pr: 0,
      },
      '&.ellipsis': {
        maxWidth: '160px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      },
    },
  }

  logger.dev('Debug log', { value: { updates } })

  return loading && !data ? (
    <CircularProgress size={24} />
  ) : (
    <Box sx={containerSx}>
      <Row className="property-header top">
        <Typography variant="h3">{propertyDefinition.name}</Typography>
        <Typography className="property-test-description">
          {propertyDefinition.description}
        </Typography>
      </Row>
      {testCreateOpen ? (
        <>
          <Row className="property-header">
            <Typography variant="h3">Create Test</Typography>
            <Row className="property-header-buttons">
              <SidebarButton
                label="Add to test"
                icon={<IconPlus />}
                onClick={() => {
                  setTestObjectSearchOpen(true)
                }}
              />
              {testObjectIds.length > 0 && (
                <SidebarButton
                  label="Run Test"
                  icon={<IconTestPipe />}
                  onClick={handleRunTest}
                />
              )}
            </Row>
          </Row>
          <Table>
            <TableBody>
              {testObjectIds.map((objectId) => (
                <TableRow key={objectId}>
                  <TableCell sx={{ width: '24px' }}>
                    <IconButton
                      onClick={() => {
                        setTestObjectIds((prev) =>
                          prev.filter((id) => id !== objectId)
                        )
                      }}
                    >
                      <IconX size={16} />
                    </IconButton>
                  </TableCell>
                  <TableCell>
                    <ObjectChip
                      crmObject={{
                        objectId,
                        objectType: propertyDefinition.objectTypeId,
                        workspaceId,
                      }}
                      workspaceId={workspaceId}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <SearchModal
            open={testObjectSearchOpen}
            onClose={() => {
              setTestObjectSearchOpen(false)
            }}
            onSelect={(searchResult) => {
              setTestObjectIds((prev) => [...prev, searchResult.objectId])
            }}
            allowCreation={true}
            forcedObjectTypes={[propertyDefinition.objectTypeId]}
            workspaceId={workspaceId}
          />
        </>
      ) : (
        <>
          {selectedRunId ? (
            <>
              <Row className="property-header">
                <Row sx={{ alignItems: 'center' }}>
                  <IconButton onClick={handleBackToAllHistory}>
                    <IconArrowLeft size={16} />
                  </IconButton>
                  <Typography
                    variant="h3"
                    sx={{ ml: 1 }}
                  >
                    Backfill Run Details
                  </Typography>
                </Row>
                <Row className="property-header-buttons">
                  <IconButton
                    onClick={() => {
                      refetch()
                    }}
                  >
                    <IconRefresh size={16} />
                  </IconButton>
                </Row>
              </Row>

              {backfills && backfills[0] && (
                <Table>
                  <TableBody>
                    <TableRow>
                      <TableCell>Run ID:</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        {backfills[0].id}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Status:</TableCell>
                      <TableCell>
                        <MetadataChip
                          state={{
                            label: statusLabels[backfills[0].status],
                            value: backfills[0].status,
                            color:
                              backfills[0].status === 'COMPLETED'
                                ? 'success'
                                : backfills[0].status === 'FAILED'
                                  ? 'error'
                                  : backfills[0].status === 'PENDING'
                                    ? 'primary'
                                    : 'default',
                          }}
                          disabled={true}
                        />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Created:</TableCell>
                      <TableCell>
                        {dayjs(backfills[0].createdAt).format(
                          'MMMM D, YYYY h:mm A'
                        )}{' '}
                        ({dayjs(backfills[0].createdAt).fromNow()})
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>User:</TableCell>
                      <TableCell>
                        <WorkspaceMemberChip
                          workspaceId={workspaceId}
                          userId={backfills[0].userId}
                        />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Type:</TableCell>
                      <TableCell>
                        <MetadataChip
                          state={{
                            label: backfills[0].metadata?.isTest
                              ? 'Test'
                              : 'Backfill',
                            value: backfills[0].metadata?.isTest,
                          }}
                          icon={<IconChartColumn size={8} />}
                        />
                      </TableCell>
                    </TableRow>
                    {backfills[0].metadata?.testObjectIds && (
                      <TableRow>
                        <TableCell>Test Objects:</TableCell>
                        <TableCell>
                          {backfills[0].metadata.testObjectIds.length} object(s)
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              )}

              <Row className="property-header">
                <Typography variant="h3">Results</Typography>
              </Row>

              <PropertyHistoryEntries
                updates={updates}
                propertyDefinition={propertyDefinition}
                workspaceId={workspaceId}
                onPropertyValueClick={handlePropertyValueClick}
              />
            </>
          ) : (
            <>
              {/* Show TestGuidance when no tests have been run yet */}
              {showGuidance &&
                (!backfills || backfills.length === 0) &&
                (propertyDefinition.aiManaged || propertyDefinition.useWeb) &&
                !showTestResults && (
                  <TestGuidance
                    propertyDefinition={propertyDefinition}
                    onStartTest={() => {
                      setTestCreateOpen(true)
                      setShowGuidance(false)
                    }}
                    isFirstTime={isFirstVisitRef.current}
                  />
                )}

              {/* Show Test Results Review */}
              {showTestResults && testResults.length > 0 && (
                <TestResultsReview
                  results={testResults}
                  propertyDefinition={propertyDefinition}
                  onRefineDefinition={() => {
                    setShowTestResults(false)
                    if (onSwitchToEdit) {
                      onSwitchToEdit()
                    }
                  }}
                  onRunBackfill={handleRunBackfill}
                />
              )}

              <Row className="property-header">
                <Typography variant="h3">Backfills & Tests</Typography>
                <Row className="property-header-buttons">
                  {(!showGuidance || backfills?.length > 0) && (
                    <SidebarButton
                      label="Create Test"
                      icon={<IconTestPipe />}
                      onClick={() => {
                        setTestCreateOpen(true)
                        setShowGuidance(false)
                      }}
                    />
                  )}
                  <Tooltip
                    title={
                      !propertyDefinition.useWeb &&
                      !propertyDefinition.aiManaged
                        ? 'Enable AI or Web to use backfill'
                        : !hasSuccessfulTest
                          ? 'Run a successful test first (80%+ success rate)'
                          : ''
                    }
                  >
                    <span>
                      <SidebarButton
                        label="Run Backfill"
                        icon={<IconChartColumn />}
                        onClick={handleRunBackfill}
                        disabled={
                          (!propertyDefinition.useWeb &&
                            !propertyDefinition.aiManaged) ||
                          !hasSuccessfulTest
                        }
                      />
                    </span>
                  </Tooltip>
                </Row>
              </Row>
              {backfills && backfills.length > 0 ? (
                <Table>
                  <TableBody>
                    {backfills.map((backfill) => (
                      <TableRow
                        key={backfill.id}
                        hover
                        onClick={() => {
                          if (process.env.HOST.includes('localhost')) {
                            handleViewRunDetails(backfill.id)
                          }
                        }}
                      >
                        <TableCell>
                          <WorkspaceMemberChip
                            workspaceId={workspaceId}
                            userId={backfill.userId}
                          />
                        </TableCell>
                        <TableCell>
                          <MetadataChip
                            state={{
                              label: backfill.metadata?.isTest
                                ? 'Test'
                                : 'Backfill',
                              value: backfill.metadata?.isTest,
                            }}
                            icon={<IconChartColumn size={8} />}
                            tooltipText={
                              backfill.metadata?.isTest
                                ? 'Test run'
                                : 'Backfill of all records'
                            }
                            disabled={true}
                          />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          {dayjs(backfill.createdAt).fromNow()}
                        </TableCell>
                        <TableCell sx={{ width: '112px' }}>
                          <MetadataChip
                            state={{
                              label: statusLabels[backfill.status],
                              value: backfill.status,
                              color:
                                backfill.status === 'COMPLETED'
                                  ? 'success'
                                  : backfill.status === 'FAILED'
                                    ? 'error'
                                    : backfill.status === 'PENDING'
                                      ? 'primary'
                                      : 'default',
                            }}
                            disabled={true}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <Typography>
                  No backfills run for {propertyDefinition.name}
                </Typography>
              )}

              <Row className="property-header update-history-header">
                <Typography variant="h3">Update History</Typography>
                <Row className="property-header-buttons">
                  <IconButton
                    onClick={() => {
                      refetch()
                    }}
                  >
                    <IconRefresh size={16} />
                  </IconButton>
                </Row>
              </Row>

              <PropertyHistoryEntries
                updates={updates}
                propertyDefinition={propertyDefinition}
                workspaceId={workspaceId}
                onPropertyValueClick={handlePropertyValueClick}
              />
            </>
          )}
        </>
      )}

      {propertyEditState.loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <CircularProgress size={20} />
        </Box>
      )}

      {propertyEditState.open && (
        <PropertyEdit
          propertyDefinition={propertyDefinition}
          objectId={propertyEditState.objectId}
          objectType={propertyDefinition.objectTypeId}
          workspaceId={workspaceId}
          value={propertyEditState.propertyValue || propertyEditState.value}
          onUpdate={(updatedValue) => {
            logger.dev('Property updated from history view', { updatedValue })
            setPropertyEditState((prevState) => ({
              ...prevState,
              propertyValue: updatedValue,
            }))
            refetch()
          }}
          onClose={handlePropertyEditClose}
          targetElementId={propertyEditState.targetId}
        />
      )}
    </Box>
  )
}

export default PropertyTest
